import { Field } from '@today/ui-components'
import { Badge, Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/** Translate a catalog key (react-i18next's `t`, or a stub in stories/tests). */
export type RecipientStepTranslate = (key: string, vars?: Record<string, string | number>) => string

/** A typeahead candidate, already mapped for display by the step hook. */
export interface RecipientCandidateView {
	id: string
	name: string
	type: 'lead' | 'customer'
	/** The last offer's date, already formatted for the locale; null when the customer has none. */
	lastOfferDate: string | null
}

/** The inline prospect form's values. `marketingConsent` starts unchecked. */
export interface ProspectFormValues {
	firstName: string
	lastName: string
	email: string
	phone: string
	marketingConsent: boolean
}

/**
 * Props for {@link RecipientStepView} — offer wizard Step 1 (TO-122). Searching,
 * debouncing, prospect creation and error mapping live in the step hook; this
 * view only renders their results and reports user intent.
 *
 * @property searchHint - True while the query is too short to search (fewer than 2 characters).
 * @property prospectError - The server refusal, already translated; the form keeps its values.
 */
export interface RecipientStepViewProps {
	t: RecipientStepTranslate
	query: string
	searchHint: boolean
	candidates: RecipientCandidateView[]
	isSearching: boolean
	selected: { id: string; name: string } | null
	prospectOpen: boolean
	prospect: ProspectFormValues
	prospectError: string | null
	isCreating: boolean
	onQueryChange: (query: string) => void
	onSelect: (candidate: RecipientCandidateView) => void
	onClearSelection: () => void
	onOpenProspect: () => void
	onCancelProspect: () => void
	onProspectChange: (field: keyof ProspectFormValues, value: string | boolean) => void
	onSubmitProspect: () => void
}

function SelectedRecipient({ props }: { props: RecipientStepViewProps }) {
	const { t, selected } = props
	if (!selected) return null
	return (
		<div className="flex items-center gap-3" aria-live="polite">
			<p className="text-ds-sm text-ds-text">
				{t('recipient.selected')}: <strong>{selected.name}</strong>
			</p>
			<Button size="sm" variant="muted" onPress={props.onClearSelection}>
				{t('recipient.change')}
			</Button>
		</div>
	)
}

function CandidateList({ props }: { props: RecipientStepViewProps }) {
	const { t, candidates } = props
	if (props.searchHint) return <p className="text-ds-sm text-ds-text-muted">{t('recipient.searchHint')}</p>
	if (props.isSearching) return <p className="text-ds-sm text-ds-text-muted">{t('recipient.searching')}</p>
	if (props.query && candidates.length === 0) {
		return <p className="text-ds-sm text-ds-text-muted">{t('recipient.noMatches')}</p>
	}
	return (
		<ul aria-label={t('recipient.results')} className="flex flex-col gap-1">
			{candidates.map((candidate) => (
				<li key={candidate.id}>
					<Button
						variant="muted"
						className="flex w-full items-center justify-between gap-2"
						onPress={() => props.onSelect(candidate)}
					>
						<span>{candidate.name}</span>
						<span className="flex items-center gap-2 text-ds-sm">
							<Badge size="sm">{t(`recipient.type.${candidate.type}`)}</Badge>
							{candidate.lastOfferDate
								? t('recipient.lastOffer', { date: candidate.lastOfferDate })
								: t('recipient.noOffers')}
						</span>
					</Button>
				</li>
			))}
		</ul>
	)
}

const PROSPECT_TEXT_FIELDS: Array<{ field: 'firstName' | 'lastName' | 'email' | 'phone'; type: string }> = [
	{ field: 'firstName', type: 'text' },
	{ field: 'lastName', type: 'text' },
	{ field: 'email', type: 'email' },
	{ field: 'phone', type: 'tel' },
]

function ProspectFields({ props }: { props: RecipientStepViewProps }) {
	const { t, prospect, onProspectChange } = props
	return (
		<>
			{PROSPECT_TEXT_FIELDS.map(({ field, type }) => (
				<Field
					key={field}
					label={t(`prospect.${field}`)}
					type={type}
					value={prospect[field]}
					onChange={(e) => onProspectChange(field, e.target.value)}
				/>
			))}
			<label className="flex items-center gap-2 text-ds-sm text-ds-text">
				<input
					type="checkbox"
					checked={prospect.marketingConsent}
					onChange={(e) => onProspectChange('marketingConsent', e.target.checked)}
				/>
				{t('prospect.marketingConsent')}
			</label>
		</>
	)
}

function ProspectForm({ props }: { props: RecipientStepViewProps }) {
	const { t } = props
	return (
		<form
			aria-label={t('prospect.title')}
			className="flex flex-col gap-2"
			onSubmit={(event) => {
				event.preventDefault()
				props.onSubmitProspect()
			}}
		>
			<ProspectFields props={props} />
			{props.prospectError ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{props.prospectError}
				</p>
			) : null}
			<div className="flex gap-2">
				<Button type="submit" isDisabled={props.isCreating}>
					{t('prospect.create')}
				</Button>
				<Button variant="muted" onPress={props.onCancelProspect}>
					{t('prospect.cancel')}
				</Button>
			</div>
		</form>
	)
}

/** Offer wizard Step 1 — pick an existing recipient or create a prospect inline (organism). */
export function RecipientStepView(props: RecipientStepViewProps): ReactElement {
	const { t } = props
	return (
		<section aria-labelledby="recipient-step-title" className="flex flex-col gap-4">
			<h2 id="recipient-step-title" className="text-ds-lg font-semibold text-ds-text">
				{t('recipient.title')}
			</h2>
			<SelectedRecipient props={props} />
			<Field
				label={t('recipient.search')}
				type="search"
				value={props.query}
				onChange={(e) => props.onQueryChange(e.target.value)}
			/>
			<CandidateList props={props} />
			{props.prospectOpen ? (
				<ProspectForm props={props} />
			) : (
				<Button variant="secondary" onPress={props.onOpenProspect}>
					{t('prospect.open')}
				</Button>
			)}
		</section>
	)
}
